import {Box, HBox} from '../../../../../src/components/core/view';
import {CustomText, H5Text} from '../../../../../src/components/core/text';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  SubstitutionItemParams,
  SubstitutionOptions,
} from '../../../../../src/itemDetails/types/itemReplacement';
import {getClientType, getStoreName} from '@buncha/common/types/common';
import {
  useItemReplacementContext,
  useSubstituteItemInRun,
} from '../../../../../src/itemDetails/hooks/itemReplacement';

import {Analytics} from '@buncha/config/analytics/analytics';
import AppModal from '@buncha/common/components/CustomModal/AppModal';
import {CustomFlatList} from '../../../../../src/components/core/list';
import {Divider} from 'native-base';
import {EventType} from '@buncha/config/analytics/type';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '@buncha/itemDetails/navigation/types';
import {Modules} from '@buncha/utils/types';
import {StyleSheet} from 'react-native';
import SubstitutionOptionCell from '../substitutionOptionCell';
import {isWeb} from '@buncha/utils/common';
import {navigation} from '@buncha/config/navigation';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface RouteParamsType {
  runId?: number;
  isMeijerStore?: boolean;
  storeId?: number;
  storeIndex?: string;
  orderId?: number;
}

interface Props {
  hasSubstituteAdded?: boolean;
}

const BuyerReplacementSuggestions: React.FC<Props> = props => {
  const {state} = useItemReplacementContext();

  const {substituteItemInRun, itemSubstituteLoading, errorMessage} =
    useSubstituteItemInRun();
  const {title, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const theme = useColorThemeType();

  const subOptions = state?.item?.substitutionOptions || [];

  const route = useRoute();
  const routeParams: RouteParamsType | undefined = route?.params;
  const runId = routeParams?.runId || 0;
  const isMeijerStore = routeParams?.isMeijerStore ?? false;

  const [showErrorModal, setSetshowErrorModal] = useState('');

  const handleOnClose = () => {
    setSetshowErrorModal('');
  };

  useEffect(() => {
    if (errorMessage) setSetshowErrorModal(errorMessage);
  }, [errorMessage]);

  const onPressHandler = useCallback(
    async (substituteParams: SubstitutionItemParams) => {
      if (isMeijerStore) {
        navigation.navigate(ShopperScreens.ScannedAddItemPage, {
          storeIndex: routeParams?.storeIndex,
          runId: routeParams?.runId,
          orderId: routeParams?.orderId,
          storeId: routeParams?.storeId,
          plu: substituteParams?.upcInput?.upcOriginal,
          quantity: substituteParams?.quantity,
          substituteParams: substituteParams,
          returnPage: ShopperScreens.BatchDetailsPage,
        });
        return;
      }
      const response = await substituteItemInRun(substituteParams);
      if (response) {
        Analytics.eventWithProps('Add Substitute', {
          type: EventType.cta,
          source: 'Add Replacement Page',
          runId: routeParams?.runId,
          client: getClientType(isMeijerStore),
          storeType: getStoreName(String(routeParams?.storeId)),
        });
        if (isWeb())
          navigation.navigate(HomeScreens.Shopper, {
            screen: ShopperScreens.BatchDetailsPage,
            params: {runId: routeParams?.runId},
          });
        else
          navigation.navigate(ShopperScreens.ItemDetails, {
            screen: ItemDetailsScreen.ItemScanner,
            params: {
              orderItemId: substituteParams?.orderItemId,
              storeIndex: routeParams?.storeIndex,
              runId: routeParams?.runId,
              quantity: substituteParams?.quantity,
              source: 'Replacement Item Modal',
            },
          });
      }
    },
    [
      isMeijerStore,
      routeParams?.orderId,
      routeParams?.runId,
      routeParams?.storeId,
      routeParams?.storeIndex,
      substituteItemInRun,
    ],
  );

  const renderItem = useCallback(
    (subItem: {item: SubstitutionOptions}) => {
      return (
        <SubstitutionOptionCell
          itemInfo={subItem?.item}
          onPressHandler={onPressHandler}
          loading={itemSubstituteLoading}
          runId={runId}
          orderId={routeParams?.orderId}
          showReplaceCTAWithCell={subOptions?.length > 1}
        />
      );
    },
    [
      itemSubstituteLoading,
      onPressHandler,
      routeParams?.orderId,
      runId,
      subOptions?.length,
    ],
  );

  const getTitle = useMemo(() => {
    if (isMeijerStore) {
      if (props?.hasSubstituteAdded)
        return "This item is unavailable. The customer has preselected a backup. Substitute with the chosen option or drop it if it's not available.";
      return 'This item is out of stock; the customer has asked for a suitable backup to be suggested.';
    }

    return "If this item is out of stock, please replace with one of the customer's suggested items";
  }, [isMeijerStore, props?.hasSubstituteAdded]);

  const separator = useCallback(() => <Divider />, []);

  return (
    <Box style={styles.parentBox}>
      <CustomText style={[styles.text]}>{getTitle}</CustomText>
      <CustomFlatList
        showsVerticalScrollIndicator={false}
        data={subOptions}
        renderItem={renderItem}
        ItemSeparatorComponent={separator}
      />
      <If condition={Boolean(showErrorModal)}>
        <AppModal
          isOpen={true}
          onClose={handleOnClose}
          topCTAHandelr={handleOnClose}
          title={title?.info}
          hideBottomCta
          modalBody={
            <HBox style={styles.modalBody}>
              <H5Text>{showErrorModal}</H5Text>
            </HBox>
          }
          ctaTopTitle={button?.ok}
          topCTAColor={theme?.background.green[100]}
          storeId={routeParams?.storeId}
        />
      </If>
    </Box>
  );
};

export default React.memo(BuyerReplacementSuggestions);

const styles = StyleSheet.create({
  text: {
    textAlign: 'left',
    marginBottom: 15,
  },
  parentBox: {
    // flex: 1,
  },
  modalBody: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});
